/**
 * discount-code-status.js
 *
 * Reports whether a discount code carried in from an email deep link actually landed.
 *
 * Shopify gives no server-side signal for this: /discount/CODE?redirect=… is a 302, so
 * document.referrer is empty or the ESP's tracking domain by the time we run — never
 * '/discount/'. The only reliable marker is a param we put on the landing URL ourselves
 * (`dsc`), cross-referenced against /cart.js.
 *
 * MEASURED BEHAVIOUR — athleticbrewing.com, 2026-09-01, against the live NextRound BXGY
 * discount (buy 3 get 1, collection-scoped, usesPerOrderLimit 1):
 *
 *   1. discount_codes is NEVER []. Shopify echoes back whatever code was thrown at it, real
 *      or fabricated, as { code, applicable: false }, and keeps doing so forever. A made-up
 *      code sitting in a 4-item cart is byte-identical to a valid code blocked by Club
 *      pricing. There is therefore NO client-side way to tell "expired / already redeemed"
 *      apart from "won't combine" — hence the single `blocked` state below rather than the
 *      separate invalid/conflict states this was first designed with.
 *   2. Codes ACCUMULATE on the session and /cart/clear does not remove them. Hence the
 *      prefix filter and the exact-code preference when picking an entry.
 *   3. An empty cart reports applicable: false for a perfectly good code — there are no line
 *      items to allocate against. So on an empty cart nothing at all is knowable, which is
 *      what `armed` exists to cover.
 *   4. Extra params DO survive /discount/CODE?redirect=… — no nested encoding needed.
 *   5. The entries carry no `amount` field, so a confirmed value must come from
 *      cart.total_discount.
 *   6. 3 items => applicable false, total_discount 0. The 4th is what makes it pay out, and
 *      the discount lands on the CHEAPEST line (verified: $9.74 discounted, not a $14.99).
 */

const DISCOUNT_STATUS_STORAGE_ATTEMPT = 'abc:discount-attempt';
const DISCOUNT_STATUS_STORAGE_REAPPLIED = 'abc:discount-reapplied';

class DiscountCodeStatus extends HTMLElement {
  constructor() {
    super();

    this.codePrefix = (this.getAttribute('data-code-prefix') || '').toUpperCase();
    this.offerId = this.getAttribute('data-offer-id') || '';
    this.totalSlots = parseInt(this.getAttribute('data-total-slots'), 10) || 0;
    this.applyPath = this.getAttribute('data-apply-path') || '';
    // 'redirect' (/discount/CODE?redirect=…) or 'param' (?discount=CODE). Defaults to the
    // form that is certain to work; switch to 'param' once Step 0 #2 confirms the short one.
    this.applyMode = this.getAttribute('data-apply-mode') || 'redirect';
    this.manualEntryEnabled = this.getAttribute('data-manual-entry') !== 'false';
    this.helpUrl = this.getAttribute('data-help-url') || '';

    // Only the failure gets a banner. Success and progress are already legible in the footer
    // — the count label and the savings line both react — so a bar above the hero on a happy
    // path is noise sitting in the most valuable space on the page. The other states are still
    // computed and dispatched; they just don't paint here.
    this.messages = {
      blocked: this.getAttribute('data-msg-blocked') || '',
    };

    this.state = null;
    this.cart = null;

    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.onReapplyClick = this.onReapplyClick.bind(this);
    this.onManualToggle = this.onManualToggle.bind(this);
    this.onManualSubmit = this.onManualSubmit.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.banner = this.querySelector('[data-dcs-banner]');
    this.messageEl = this.querySelector('[data-dcs-message]');
    this.actionsEl = this.querySelector('[data-dcs-actions]');
    this.reapplyButton = this.querySelector('[data-dcs-reapply]');
    this.helpLink = this.querySelector('[data-dcs-help]');
    this.manualEl = this.querySelector('[data-dcs-manual]');
    this.manualToggle = this.querySelector('[data-dcs-manual-toggle]');
    this.manualForm = this.querySelector('[data-dcs-form]');
    this.manualInput = this.querySelector('[data-dcs-input]');

    this.attemptedCode = this._resolveAttemptedCode();

    this.reapplyButton?.addEventListener('click', this.onReapplyClick);
    this.manualToggle?.addEventListener('click', this.onManualToggle);
    this.manualForm?.addEventListener('submit', this.onManualSubmit);

    // The theme's enriched cart (window.__abc.ShopifyCartData) comes from the
    // cart.metafields view, whose `{{ cart | json }}` is a Liquid drop that does not carry
    // discount_codes[].applicable. It also never fires on an empty cart. So we read /cart.js
    // ourselves and only use cartUpdateEnriched as a change signal.
    window.addEventListener('cartUpdateEnriched', this.handleCartUpdate);

    this.refresh();
  }

  disconnectedCallback() {
    window.removeEventListener('cartUpdateEnriched', this.handleCartUpdate);
    this.reapplyButton?.removeEventListener('click', this.onReapplyClick);
    this.manualToggle?.removeEventListener('click', this.onManualToggle);
    this.manualForm?.removeEventListener('submit', this.onManualSubmit);
    clearTimeout(this._refreshTimer);
    this.initialized = false;
  }

  handleCartUpdate() {
    clearTimeout(this._refreshTimer);
    this._refreshTimer = setTimeout(() => this.refresh(), 150);
  }

  async refresh() {
    let cart = null;
    try {
      const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`);
      if (response.ok) cart = await response.json();
    } catch (err) {
      console.warn('discount-code-status: could not read the cart', err);
    }

    // A failed read must not invent a verdict — leave whatever is on screen alone.
    if (!cart) return;

    this.cart = cart;
    this.render(this._deriveState(cart));
  }

  /* ------------------------------------------------------------------ state */

  /**
   * The code the shopper arrived with. `dsc` is ours; `discount` covers the shorter link
   * format and anyone pasting a Shopify-native discount URL. sessionStorage keeps it alive
   * across in-session navigation, since the param is gone once they click through to a PDP
   * and come back.
   */
  _resolveAttemptedCode() {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('dsc') || params.get('discount');

    if (fromUrl && fromUrl.trim()) {
      const code = fromUrl.trim();
      this._writeStorage(DISCOUNT_STATUS_STORAGE_ATTEMPT, code);
      return code;
    }

    return this._readStorage(DISCOUNT_STATUS_STORAGE_ATTEMPT);
  }

  _matchesPrefix(code) {
    if (!code) return false;
    if (!this.codePrefix) return true;
    return code.toUpperCase().startsWith(this.codePrefix);
  }

  /**
   * Quantity of items the offer can actually count. Prefers lines this builder tagged, so a
   * cart holding unrelated products doesn't inflate the total; falls back to the cart-wide
   * count when nothing is tagged (e.g. items added from a PDP rather than the builder).
   */
  _eligibleQuantity(cart) {
    const items = Array.isArray(cart.items) ? cart.items : [];
    const tagged = items
      .filter((item) => item.properties && item.properties._offer === this.offerId)
      .reduce((sum, item) => sum + (item.quantity || 0), 0);

    return tagged > 0 ? tagged : cart.item_count || 0;
  }

  _discountAmountCents(cart, entry) {
    if (entry && typeof entry.amount === 'number' && entry.amount > 0) return entry.amount;
    return cart.total_discount || 0;
  }

  _deriveState(cart) {
    const code = this.attemptedCode;

    // Silent for organic traffic — no code was ever offered, so there is nothing to report.
    if (!code) return { state: 'none' };

    const codes = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
    const relevant = codes.filter((entry) => this._matchesPrefix(entry.code));
    // Prefer the exact code we arrived with. Falling back to any same-prefix code keeps a
    // case mismatch reporting correctly; the prefix filter is what stops an unrelated
    // sitewide code in the cart from being announced as this offer.
    const entry =
      relevant.find((item) => (item.code || '').toUpperCase() === code.toUpperCase()) ||
      relevant[0] ||
      null;

    // The only unambiguous signal Shopify gives us.
    if (entry && entry.applicable) {
      return {
        state: 'applied',
        code: entry.code,
        amountCents: this._discountAmountCents(cart, entry),
      };
    }

    // An empty cart reports applicable: false for good and bad codes alike — there is nothing
    // to allocate against yet, so no verdict is available. Restate the offer and let the first
    // selection produce real evidence. This is the state an email click lands in, so calling
    // it a failure here would condemn every valid code in the campaign.
    if ((cart.item_count || 0) === 0) return { state: 'armed', code: entry ? entry.code : code };

    const eligible = this._eligibleQuantity(cart);
    if (this.totalSlots > 0 && eligible < this.totalSlots) {
      return {
        state: 'pending',
        code: entry ? entry.code : code,
        remaining: this.totalSlots - eligible,
      };
    }

    // Enough in the cart and still not paying out. The cause is genuinely undecidable from
    // here — Shopify reports a fabricated code exactly as it reports a real one that can't
    // combine (see finding 1) — so the copy has to name both possibilities and offer both
    // remedies rather than guess.
    return { state: 'blocked', code: entry ? entry.code : code };
  }

  /* ----------------------------------------------------------------- render */

  _format(template, tokens) {
    return Object.keys(tokens).reduce(
      (text, key) => text.split(`{${key}}`).join(tokens[key]),
      template || ''
    );
  }

  _formatMoney(cents) {
    return `$${((cents || 0) / 100).toFixed(2)}`;
  }

  render(result) {
    const { state } = result;
    this.state = state;

    const message = this._format(this.messages[state], {
      code: result.code || '',
      remaining: result.remaining || 0,
      amount: this._formatMoney(result.amountCents),
    });

    if (this.banner) {
      const showBanner = state === 'blocked' && message !== '';
      this.banner.hidden = !showBanner;
      this.banner.setAttribute('data-state', state);
    }
    if (this.messageEl) this.messageEl.textContent = message;

    const showReapply = state === 'blocked' && !this._alreadyReapplied();
    if (this.reapplyButton) this.reapplyButton.hidden = !showReapply;

    const showHelp = this.helpUrl !== '' && state === 'blocked';
    if (this.helpLink) this.helpLink.hidden = !showHelp;

    if (this.actionsEl) this.actionsEl.hidden = !showReapply && !showHelp;

    // Manual entry is the recovery path: offered when the code failed, and as one quiet line
    // for anyone who has a code but didn't arrive through the link.
    const showManual = this.manualEntryEnabled && (state === 'blocked' || state === 'none');
    if (this.manualEl) this.manualEl.hidden = !showManual;

    this.dispatchEvent(
      new CustomEvent('abc:discount-status', {
        detail: {
          state,
          code: result.code || '',
          message,
          amountCents: result.amountCents || 0,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  /* ---------------------------------------------------------------- actions */

  /**
   * Attaching a code requires a page load — Shopify has no Ajax equivalent — so every
   * recovery path here is a navigation.
   */
  _applyUrl(code) {
    const encoded = encodeURIComponent(code);
    const path = this.applyPath || window.location.pathname;

    if (this.applyMode === 'param') {
      return `${path}?discount=${encoded}&dsc=${encoded}`;
    }
    return `/discount/${encoded}?redirect=${path}&dsc=${encoded}`;
  }

  _alreadyReapplied() {
    if (!this.attemptedCode) return false;
    return this._readStorage(DISCOUNT_STATUS_STORAGE_REAPPLIED) === this.attemptedCode;
  }

  onReapplyClick() {
    if (!this.attemptedCode || this._alreadyReapplied()) return;
    // Record before navigating so a code that fails twice can't bounce the shopper in a loop.
    this._writeStorage(DISCOUNT_STATUS_STORAGE_REAPPLIED, this.attemptedCode);
    window.location.assign(this._applyUrl(this.attemptedCode));
  }

  onManualToggle() {
    if (!this.manualForm) return;
    const opening = this.manualForm.hidden;
    this.manualForm.hidden = !opening;
    this.manualToggle?.setAttribute('aria-expanded', String(opening));
    if (opening) this.manualInput?.focus();
  }

  onManualSubmit(event) {
    event.preventDefault();
    const code = (this.manualInput?.value || '').trim().toUpperCase();
    if (!code) return;
    window.location.assign(this._applyUrl(code));
  }

  /* ---------------------------------------------------------------- storage */

  // Private browsing and blocked site data make these throw rather than return null.
  _readStorage(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (err) {
      return null;
    }
  }

  _writeStorage(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (err) {
      /* no-op — the banner still works, it just won't survive navigation */
    }
  }
}

if (!customElements.get('discount-code-status'))
  customElements.define('discount-code-status', DiscountCodeStatus);
